import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Tabs from '@cloudscape-design/components/tabs';
import { toast } from '@/lib/notifications';
import { useTenantDetail } from '@/features/tenants/useTenantDetail';
import { useTenantDeletable } from '@/features/tenants/useTenantDeletable';
import { TenantInfoCard } from '@/features/tenants/TenantInfoCard';
import { DemoFlagDialog } from '@/features/tenants/DemoFlagDialog';
import { TenantDeleteTab } from '@/features/tenants/TenantDeleteTab';
import { TenantLocationTab } from '@/features/tenants/TenantLocationTab';
import { BillingCard } from '@/features/billing/BillingCard';
import { BillingActions } from '@/features/billing/BillingActions';
import { CheckoutLinksCard } from '@/features/billing/CheckoutLinksCard';
import { BillingAddonsCard } from '@/features/billing/BillingAddonsCard';
import { CopyValue } from '@/components/CopyValue';
import { UsageCard } from '@/features/usage/UsageCard';
import { UsageTab } from '@/features/usage/UsageTab';
import { TenantAuditTab } from '@/features/audit/TenantAuditTab';
import { TenantFinanceTab } from '@/features/finance/TenantFinanceTab';
import { LabelTemplatesTab } from '@/features/labelTemplates/LabelTemplatesTab';
import { ProductsTab } from '@/features/products/ProductsTab';
import { PrintersTab } from '@/features/printers/PrintersTab';
import { MembersTab } from '@/features/members/MembersTab';
import { ResponsiblesTab } from '@/features/responsibles/ResponsiblesTab';

type Tab =
  | 'overview'
  | 'location'
  | 'billing'
  | 'finance'
  | 'usage'
  | 'products'
  | 'templates'
  | 'printers'
  | 'users'
  | 'responsibles'
  | 'audit'
  | 'delete';

/** Spinner centralizado das abas enquanto o tenant não carregou. */
function LoadingBox(): React.JSX.Element {
  return (
    <Box textAlign="center" padding="l">
      <Spinner size="large" />
    </Box>
  );
}

/**
 * Tela `/tenants/:id` — detalhe de uma empresa em abas: visão geral,
 * localização, cobrança, financeiro, uso, produtos, modelos, impressoras,
 * usuários, responsáveis, auditoria e excluir.
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 (Sprint 2, detalhe de
 * tenant com aba Visão geral).
 *
 * Contrato: `useTenantDetail(id)` assina em tempo real o tenant, a assinatura
 * e o uso do mês; `useTenantDeletable(id)` decide se a aba "Excluir" aparece.
 * Cada aba de dado (produtos, membros, impressoras…) tem o próprio hook e só
 * monta quando o tenant carregou. Erro de carga → toast; tenant inexistente →
 * `Alert`. A escrita fica nos diálogos e cards (demo, cobrança, membros,
 * exclusão), sempre por callable.
 *
 * Usado por: rota `tenants/:id` (`routes/router.tsx`); chegam aqui a lista de
 * tenants, a tela de Usuários, a fila de vendas sem pagamento e o
 * `TenantsPage` depois de criar cortesia. Spec:
 * `docs/screens/tenant-detail-admin.md`.
 *
 * Dados: `tenants/{tenantId}`, `tenants/{tenantId}/billing/subscription`
 * (plano efetivo e pricing congelado) e o uso mensal — detalhes em
 * `features/tenants/useTenantDetail.ts`. Leitura cross-tenant permitida pelo
 * claim `superAdmin` nas rules.
 *
 * Dicas: a aba "Excluir" aparece em qualquer estado resolvido da contagem
 * (vazio → hard-delete simples; com dados → purge forçado, #83; erro → mostra
 * mesmo assim). O guard autoritativo é server-side em `hardDeleteEmptyTenant`.
 * O `key={checkoutLinksNonce}` do card de links (#289) é o que faz a lista
 * remontar com o link recém-criado — não remover.
 *
 * Issues: #83 (purge forçado), #289 (links de pagamento).
 */
export function TenantDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const tenantId = params.id ?? '';
  const detail = useTenantDetail(tenantId);
  // A aba "Excluir" aparece para qualquer estado resolvido (vazio →
  // hard-delete simples; com dados → purge forçado, issue #83; ERROR na
  // contagem client-side → mostra mesmo assim, o servidor revalida). Só fica
  // oculta enquanto LOADING. O dialog adapta a fricção; o guard autoritativo
  // é server-side em `hardDeleteEmptyTenant`.
  const deletable = useTenantDeletable(tenantId);
  const [tab, setTab] = useState<Tab>('overview');
  // `null` = dialog fechado; o booleano é o valor que será gravado.
  const [demoFlagPending, setDemoFlagPending] = useState<boolean | null>(null);
  // #289 — incrementado ao gerar link de pagamento; serve de `key` para o card
  // de links remontar e já listar o que acabou de ser criado.
  const [checkoutLinksNonce, setCheckoutLinksNonce] = useState(0);

  useEffect(() => {
    if (detail.tenant.kind === 'ERROR') {
      toast.error(t('tenantDetail.errorLoad'));
    }
  }, [detail.tenant.kind, t]);

  if (detail.tenant.kind === 'NOT_FOUND') {
    return (
      <ContentLayout header={<Header variant="h1">{t('tenants.title')}</Header>}>
        <Alert type="warning">{t('tenantDetail.notFound', { id: tenantId })}</Alert>
      </ContentLayout>
    );
  }

  const tenantData = detail.tenant.kind === 'READY' ? detail.tenant.data : null;
  const subscription = detail.billing.kind === 'READY' ? detail.billing.data : null;

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={<CopyValue value={tenantData?.id ?? tenantId} />}>
          {tenantData?.name ?? t('tenants.title')}
        </Header>
      }
    >
      <Tabs
        activeTabId={tab}
        onChange={({ detail: d }): void => {
          setTab(d.activeTabId as Tab);
        }}
        tabs={[
          {
            id: 'overview',
            label: t('tenantDetail.tabs.overview'),
            content: (
              <ColumnLayout columns={2}>
                {tenantData ? (
                  <TenantInfoCard
                    tenant={tenantData}
                    subscription={subscription}
                    billingPending={detail.billing.kind === 'LOADING'}
                    billingError={detail.billing.kind === 'ERROR'}
                    onToggleDemo={(nextValue): void => {
                      setDemoFlagPending(nextValue);
                    }}
                  />
                ) : (
                  <LoadingBox />
                )}
                <BillingCard result={detail.billing} tenantId={tenantId} />
                <UsageCard usage={detail.usage} yyyymm={detail.yyyymm} />
                <BillingAddonsCard subscription={subscription} />
              </ColumnLayout>
            ),
          },
          {
            id: 'location',
            label: t('tenantDetail.tabs.location'),
            content: tenantData ? <TenantLocationTab tenant={tenantData} /> : <LoadingBox />,
          },
          {
            id: 'billing',
            label: t('tenantDetail.tabs.billing'),
            content: tenantData ? (
              <SpaceBetween size="m">
                <BillingActions
                  tenant={tenantData}
                  subscription={subscription}
                  onCheckoutLinkCreated={(): void => {
                    setCheckoutLinksNonce((n) => n + 1);
                  }}
                />
                <BillingCard result={detail.billing} tenantId={tenantId} />
                {/* #289 — links de pagamento (sessão viva ou recuperação).
                    `key` remonta o card quando um link novo é gerado, para a
                    lista já abrir com ele. */}
                <CheckoutLinksCard
                  key={checkoutLinksNonce}
                  tenantId={tenantId}
                />
                <BillingAddonsCard subscription={subscription} />
              </SpaceBetween>
            ) : (
              <LoadingBox />
            ),
          },
          {
            id: 'finance',
            label: t('tenantDetail.tabs.finance'),
            content: tenantData ? <TenantFinanceTab tenantId={tenantData.id} /> : <LoadingBox />,
          },
          {
            id: 'usage',
            label: t('tenantDetail.tabs.usage'),
            content: tenantData ? (
              <UsageTab tenantId={tenantData.id} usage={detail.usage} yyyymm={detail.yyyymm} />
            ) : (
              <LoadingBox />
            ),
          },
          {
            id: 'products',
            label: t('tenantDetail.tabs.products'),
            content: tenantData ? <ProductsTab tenantId={tenantData.id} /> : <LoadingBox />,
          },
          {
            id: 'templates',
            label: t('tenantDetail.tabs.templates'),
            content: tenantData ? <LabelTemplatesTab tenantId={tenantData.id} /> : <LoadingBox />,
          },
          {
            id: 'printers',
            label: t('tenantDetail.tabs.printers'),
            content: tenantData ? <PrintersTab tenantId={tenantData.id} /> : <LoadingBox />,
          },
          {
            id: 'users',
            label: t('tenantDetail.tabs.users'),
            content: tenantData ? (
              <MembersTab tenantId={tenantData.id} tenantCountry={tenantData.countryCode ?? null} />
            ) : (
              <LoadingBox />
            ),
          },
          {
            id: 'responsibles',
            label: t('tenantDetail.tabs.responsibles'),
            content: tenantData ? <ResponsiblesTab tenantId={tenantData.id} /> : <LoadingBox />,
          },
          {
            id: 'audit',
            label: t('tenantDetail.tabs.audit'),
            content: tenantData ? <TenantAuditTab tenantId={tenantData.id} /> : <LoadingBox />,
          },
          ...(deletable.kind !== 'LOADING' && tenantData
            ? [
                {
                  id: 'delete',
                  label: t('tenantDetail.tabs.delete'),
                  content: (
                    <TenantDeleteTab tenantId={tenantData.id} tenantName={tenantData.name} />
                  ),
                },
              ]
            : []),
        ]}
      />
      {tenantData ? (
        <DemoFlagDialog
          open={demoFlagPending !== null}
          tenantId={tenantData.id}
          tenantName={tenantData.name}
          nextValue={demoFlagPending ?? false}
          onClose={(): void => {
            setDemoFlagPending(null);
          }}
        />
      ) : null}
    </ContentLayout>
  );
}
